import { brandCoreLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { SortOrder, updateSort } from "helpers/updateSort";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  BrandCore,
  handleSelectAll as onSelectAll,
  handlePaginate as onPaginate,
  handleSelect as onSelect,
  deleteBrandCoreItem,
  getBrandCoreList,
  bunchUpdateBrandCore,
} from "store/cms/footer/brandCore/slice";
import { openModal } from "store/modals/slice";
import CmsBrandCoreFilter from "./component/CmsBrandCoreFilter";
import CmsBrandCoreHeader from "./component/CmsBrandCoreHeader";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import { getIsActiveNumeric } from "helpers/getIsActiveNumeric";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

const BrandCoreList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [limit, setLimit] = useState<number>(brandCoreLimits[0]);
  const [filterByTitle, setFilterByTitle] = useState<string>("");
  const [filterByGeo, setFilterByGeo] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [filterByActive, setFilterByActive] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [order, setOrder] = useState<SortOrder<BrandCore>>({});

  const brandsList = useAppSelector((state) => state["cms-brandCore"].list);

  const isBrandsListFetched = useAppSelector(
    (state) => state["cms-brandCore"].isListFetched,
  );
  const totalItems = useAppSelector(
    (state) => state["cms-brandCore"].totalItems,
  );
  const totalPages = useAppSelector(
    (state) => state["cms-brandCore"].totalPages,
  );
  const activePage = useAppSelector(
    (state) => state["cms-brandCore"].activePage,
  );
  const selectedItems = useAppSelector(
    (state) => state["cms-brandCore"].selectedItems,
  );
  const processingItemId = useAppSelector(
    (state) => state["cms-brandCore"].processingItemId,
  );

  const actionOptions = [
    { label: "Activate", value: "activate" },
    { label: "Hide", value: "hide" },
  ];

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cms-brandCore',
    isListFetched: isBrandsListFetched
  });
    

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (!isBrandsListFetched) {
      dispatch(
        getBrandCoreList({
          limit,
          offset: activePage - 1,
          title: filterByTitle,
          countries: filterByGeo.map((g) => g.value as string),
          isActive: getIsActiveNumeric(filterByActive),
          order,
        }),
      );
    }
  }, [isBrandsListFetched, activePage, limit]);

  const handleOrderClick = (key: keyof typeof order) => {
    setOrder((currentOrder) => updateSort(currentOrder, key));
    dispatch(onPaginate(1));
  };

  const handleSelectAll = () => {
    dispatch(onSelectAll());
  };

  const handleSelect = (id: number) => {
    dispatch(onSelect(id));
  };

  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(onPaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(onPaginate(activePage + 1));
    }
  };

  const handleApplyFilter = () => {
    dispatch(onPaginate(1));
  };

  const handleDelete = (id: number) => {
    if (!permissions?.delete) {
      return;
    }

    dispatch(deleteBrandCoreItem(id));
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.brandCore}`);

    dispatch(openModal({ activeModal: ModalTypes.brandCore }));
  };

  const handleEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.brandCore}&entityId=${id}`,
    );

    dispatch(openModal({ activeModal: ModalTypes.brandCore }));
  };

  const handleSelectAction = async (value: string) => {
    if (!selectedItems.length) return;

    const isActive = value === "activate" ? 1 : 0;

    await dispatch(
      bunchUpdateBrandCore({
        ids: selectedItems,
        isActive,
      }),
    );
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <CmsBrandCoreFilter
        filterByTitle={filterByTitle}
        onFilterSearchByTitle={setFilterByTitle}
        filterByActive={filterByActive}
        onFilterByActive={setFilterByActive}
        filterByGeo={filterByGeo}
        onFilterByGeo={setFilterByGeo}
        handleApply={handleApplyFilter}
      />
      <div className="page-list--with-filter-content">
        <CmsBrandCoreHeader
          options={actionOptions}
          selectAction={handleSelectAction}
          createModal={handleOpenCreateModal}
          isDisabled={selectedItems.length === 0 || !permissions?.update}
          isCreateAllowed={permissions?.create}
          refresh={refresh}
          isRefreshRequested={isRefreshRequested}
        />

        {!isBrandsListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={5} />
            <PaginationSkeleton />
          </>
        )}

        {isBrandsListFetched && brandsList.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noBrandCoreFound")} />
          </div>
        )}

        {isBrandsListFetched && brandsList.length > 0 && (
          <>
            <div className="game-table-wrap">
              <Table
                data={brandsList}
                idField="id"
                order={order}
                onSort={(v) => {
                  const k = v as unknown;
                  handleOrderClick(k as keyof typeof order);
                }}
                columns={{
                  sortingColumns: {
                    geo: {
                      value: "geo",
                      hasSorting: true,
                    },
                  },
                }}
                onAllSelect={handleSelectAll}
                onSelect={handleSelect}
                selectedItems={selectedItems}
                processingId={processingItemId}
                onDelete={handleDelete}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
              />
            </div>

            <Pagination
              handleLimitClick={(e) => {
                setLimit(e);
                dispatch(onPaginate(1));
              }}
              limitsArray={brandCoreLimits}
              activePage={activePage}
              limit={limit}
              totalItems={totalItems}
              totalPages={totalPages}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePreviousClick}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BrandCoreList;
